import Checkbox from 'components/global/Checkbox';
import FormElement from 'components/global/FormElement';
import { useAppSelector } from 'redux/hooks';
import { Form, useFormikContext } from 'formik';
import getOptions from 'utils/helpers/getOptions';
import TestAreasVisualizer from '../../global/TestAreasVisualizer/index';
import WarningBanner from 'components/global/WarningBanner';
import styles from './testAreasForm.module.scss';
import * as turf from '@turf/turf';
import {
  calculateMaxDimensionsFromBounds,
  FEET_TO_METERS,
} from 'utils/coordinateConversions';
import { PlotSizingCalculator } from 'utils/PlotSizingCalculator';

interface TestAreasFormProps {
  fieldBoundaryGeoJSON?: any;
  trialBoundaryGeoJSON?: any;
  ablineGeoJSON?: any;
  plotWidthFt: number;
  plotLengthFt: number;
  bufferFt: number;
  plotBufferLengthFt?: number;
  plotBufferWidthFt?: number;
  isPlot?: boolean;
  treatmentRates?: string;
  nominalRate?: number;
  applicationUnits?: string;
  onPlotValidationChange?: (validation: {
    plotWidthValid: boolean;
    plotLengthValid: boolean;
    plotWidthError?: string;
    plotLengthError?: string;
  }) => void;
  onFieldChange?: (fieldName: string, value: any) => void;
  suggestedValues: {
    width_ft: number;
    length_ft: number;
    buffer_ft: number;
    plot_buffer_length: number;
    plot_buffer_width: number;
  };
  userModifiedFields?: Set<string>;
}

const TestAreasForm = ({
  fieldBoundaryGeoJSON,
  trialBoundaryGeoJSON,
  ablineGeoJSON,
  plotWidthFt,
  plotLengthFt,
  bufferFt,
  plotBufferLengthFt = 0,
  plotBufferWidthFt = 0,
  isPlot = true,
  treatmentRates,
  nominalRate,
  applicationUnits,
  onPlotValidationChange,
  onFieldChange,
  suggestedValues,
  userModifiedFields = new Set(),
}: TestAreasFormProps) => {
  const { values, setFieldValue } = useFormikContext<Record<string, any>>();
  const { trial } = useAppSelector((state) => state.createTrial);

  const isPlotType = values.trial_type === 'plot';

  // Check if we have equipment data to show suggestions
  const hasEquipmentData =
    trial.execution_equipment_swath_width &&
    trial.harvester_swath_width &&
    trial.execution_equipment_dgps_type &&
    trial.harvester_dgps_type;

  // Calculate effective strip length for visualization
  const getEffectiveStripLength = () => {
    if (isPlotType) {
      return plotLengthFt; // Use form value for plots
    }

    // For strips, calculate the full usable area length
    if (!fieldBoundaryGeoJSON?.features?.[0]) {
      return plotLengthFt; // Fallback to form value if no boundary
    }

    try {
      const fieldBoundary = fieldBoundaryGeoJSON.features[0];
      const bufferM = bufferFt * FEET_TO_METERS;

      // Create usable area (field boundary minus buffer)
      const usableArea = turf.buffer(fieldBoundary, -bufferM / 1000, {
        units: 'kilometers',
      });

      if (!usableArea || !usableArea.geometry) {
        return plotLengthFt; // Fallback if buffer fails
      }

      // Get bounds of the usable area
      const bbox = turf.bbox(usableArea);
      const bounds: [number, number, number, number] = [
        bbox[0],
        bbox[1],
        bbox[2],
        bbox[3],
      ];
      const { maxLengthFt } = calculateMaxDimensionsFromBounds(bounds, 0.95);

      return maxLengthFt;
    } catch (error) {
      return plotLengthFt; // Fallback to form value on error
    }
  };

  const effectiveStripLength = getEffectiveStripLength();

  // Helper to check if current value matches suggestion
  const matchesSuggestion = (
    fieldValue: number | string,
    suggestedValue: number,
  ) => {
    const numValue = Number(fieldValue);
    return !isNaN(numValue) && Math.abs(numValue - suggestedValue) < 0.1;
  };

  // Green suggested indicator component
  const SuggestedIndicator = () => (
    <span className={styles.suggestedIndicator}>Suggested</span>
  );

  // Constants for plot count estimation
  const PLOT_COUNT_WARNING_THRESHOLD = 1000;

  // Calculate field area and estimated plot count using PlotSizingCalculator
  const fieldAreaAcres =
    PlotSizingCalculator.calculateFieldAreaAcres(fieldBoundaryGeoJSON);
  const estimatedPlotCount = PlotSizingCalculator.estimatePlotCount(
    fieldAreaAcres,
    plotWidthFt,
    plotLengthFt,
    bufferFt,
    plotBufferWidthFt || 0,
    plotBufferLengthFt || 0,
    isPlotType,
  );

  const shouldShowPlotCountWarning =
    fieldBoundaryGeoJSON &&
    values.trial_type &&
    estimatedPlotCount > PLOT_COUNT_WARNING_THRESHOLD;

  return (
    <Form className={styles['test-areas-form']}>
      <div>
        <FormElement
          name="trial_type"
          direction="row"
          element="select"
          label="Plot trial or strip trial:"
          options={getOptions(['plot', 'strip'])}
          containerClass={styles.wideLabel}
        />

        {values.trial_type ? (
          <>
            <FormElement
              name="width_ft"
              direction="row"
              label={
                <span>
                  {`${isPlotType ? 'Plot' : 'Strip'} width:`}
                  {hasEquipmentData &&
                    matchesSuggestion(
                      values.width_ft,
                      suggestedValues.width_ft,
                    ) && <SuggestedIndicator />}
                </span>
              }
              rightElement="ft"
              tooltip={
                hasEquipmentData
                  ? `Suggested: ${suggestedValues.width_ft}ft based on your equipment specifications`
                  : undefined
              }
              containerClass={styles.wideLabel}
            />

            {isPlotType && (
              <FormElement
                name="length_ft"
                direction="row"
                label={
                  <span>
                    Plot length:
                    {hasEquipmentData &&
                      matchesSuggestion(
                        values.length_ft,
                        suggestedValues.length_ft,
                      ) && <SuggestedIndicator />}
                  </span>
                }
                rightElement="ft"
                tooltip={
                  hasEquipmentData
                    ? `Suggested: ${suggestedValues.length_ft}ft based on your equipment specifications`
                    : undefined
                }
                containerClass={styles.wideLabel}
              />
            )}

            <FormElement
              name="buffer_ft"
              direction="row"
              label={
                <span>
                  Field buffer:
                  {hasEquipmentData &&
                    matchesSuggestion(
                      values.buffer_ft,
                      suggestedValues.buffer_ft,
                    ) && <SuggestedIndicator />}
                </span>
              }
              rightElement="ft"
              tooltip={
                hasEquipmentData
                  ? `Suggested: ${suggestedValues.buffer_ft}ft based on your equipment. Width of region extending in from field boundary where no test plots or strips are placed. Not testing near the field boundary reduces edge effects.`
                  : 'Width of region extending in from field boundary where no test plots or strips are placed. Not testing near the field boundary reduces edge effects. Typically around 100 ft.'
              }
              containerClass={styles.wideLabel}
            />

            {isPlotType && (
              <>
                <FormElement
                  name="plot_buffer_length"
                  direction="row"
                  label={
                    <span className={styles.plotBufferLabel}>
                      Plot buffer length:
                      {hasEquipmentData &&
                        matchesSuggestion(
                          values.plot_buffer_length,
                          suggestedValues.plot_buffer_length,
                        ) && <SuggestedIndicator />}
                    </span>
                  }
                  rightElement="ft"
                  tooltip={
                    hasEquipmentData
                      ? `Suggested: ${suggestedValues.plot_buffer_length}ft based on your equipment specifications. How much distance from the start and the end of each plot should we buffer out and exclude from the plot area? Amount to remove from each end.`
                      : 'How much distance from the start and the end of each plot should we buffer out and exclude from the plot area? Amount to remove from each end.'
                  }
                  containerClass={styles.wideLabel}
                />

                <FormElement
                  name="plot_buffer_width"
                  direction="row"
                  label={
                    <span className={styles.plotBufferLabel}>
                      Plot buffer width:
                      {hasEquipmentData &&
                        matchesSuggestion(
                          values.plot_buffer_width,
                          suggestedValues.plot_buffer_width,
                        ) && <SuggestedIndicator />}
                    </span>
                  }
                  rightElement="ft"
                  tooltip={
                    hasEquipmentData
                      ? `Suggested: ${suggestedValues.plot_buffer_width}ft based on your equipment specifications. Amount to remove from each side.`
                      : 'Amount to remove from each side.'
                  }
                  containerClass={styles.wideLabel}
                />
              </>
            )}

            {!isPlotType && (
              <FormElement
                name="plot_buffer_width"
                direction="row"
                label={
                  <span className={styles.plotBufferLabel}>
                    Strip buffer width:
                    {hasEquipmentData &&
                      matchesSuggestion(
                        values.plot_buffer_width,
                        suggestedValues.plot_buffer_width,
                      ) && <SuggestedIndicator />}
                  </span>
                }
                rightElement="ft"
                tooltip={
                  hasEquipmentData
                    ? `Suggested: ${suggestedValues.plot_buffer_width}ft based on your equipment specifications. Amount to remove from each side of the strip.`
                    : 'Amount to remove from each side of the strip.'
                }
                containerClass={styles.wideLabel}
              />
            )}

            {!trial.trial_boundary_is_field_boundary && (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Checkbox
                  id="apply-buffer"
                  label="Apply trial boundary buffer to field boundary (typical)"
                  tooltip="If unchecked, the trial boundary buffer will be applied to the trial boundary, so test plots will not be allowed within the trial buffer width of the trial boundary."
                  checked={values.apply_trial_buffer_to_field_boundary}
                  onChange={() =>
                    setFieldValue(
                      'apply_trial_buffer_to_field_boundary',
                      !values.apply_trial_buffer_to_field_boundary,
                    )
                  }
                />
              </div>
            )}
          </>
        ) : null}

        {/* Plot count warning */}
        {shouldShowPlotCountWarning && (
          <div className={styles['plot-count-warning']}>
            <WarningBanner
              message={
                <div>
                  Your chosen plot sizing for this field may create a trial
                  design with over 1000 plots. Please consider increasing the
                  size of your plots.
                </div>
              }
              icon={null}
            />
          </div>
        )}
      </div>

      <div className={styles['visualizer-section']}>
        <div className={styles['visualizer-container']}>
          <TestAreasVisualizer
            fieldBoundaryGeoJSON={fieldBoundaryGeoJSON}
            trialBoundaryGeoJSON={trialBoundaryGeoJSON}
            ablineGeoJSON={ablineGeoJSON}
            plotWidthFt={plotWidthFt}
            plotLengthFt={effectiveStripLength}
            bufferFt={bufferFt}
            plotBufferLengthFt={plotBufferLengthFt}
            plotBufferWidthFt={plotBufferWidthFt}
            isPlot={isPlot}
            treatmentRates={treatmentRates}
            nominalRate={nominalRate}
            applicationUnits={applicationUnits}
            applyTrialBufferToFieldBoundary={
              values.apply_trial_buffer_to_field_boundary
            }
            onValidationChange={onPlotValidationChange}
          />
        </div>
        <p className={styles['visualizer-caption']}>
          This graphic shows one replicate (randomized set of treatment plots)
          in your field for reference. Your actual trial design, generated in
          the next step, will have multiple replicates throughout the field.
        </p>
      </div>
    </Form>
  );
};

export default TestAreasForm;
